import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { StockService, StockPagedResult } from '../../../services/stock.service';
import { StatusService } from '../../../services/status.service';
import { PermissionService, ModuleGroup } from '../../../services/permission.service';
import { PaginationOptions } from '../../../shared/interfaces/pagination.interface';
import { Stock } from '../../../models/Stock';
import { Status } from '../../../models/Status';
import { WarehousesService } from '../../../services/warehouses.service';
import { Warehouse } from '../../../models/Warehouse';

@Component({
  selector: 'app-stock',
  templateUrl: './stock.component.html',
  styleUrls: ['./stock.component.css']
})
export class StockComponent implements OnInit {
  // Configuração da tabela
  tableTitle = 'Gestão de Estoque';
  showSearch = true;
  searchPlaceholder = 'Buscar estoque...';
  clickable = true;

  // Colunas da tabela
  columns = [
    { titulo: 'Código', label: 'code' },
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Tipo', label: 'type' },
    { titulo: 'Categoria', label: 'category' },
    { titulo: 'Quantidade', label: 'currentQuantity' },
    { titulo: 'Almoxarifado', label: 'warehouseName' },
    { titulo: 'Status', label: 'status' }
  ];

  // Ações da tabela
  actions = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-pen-to-square',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Almoxarifado',
      icon: 'fas fa-warehouse',
      action: 'warehouse',
      class: 'btn-toggle'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash-can',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  // Dados
  stocks: Stock[] = [];
  warehouses: Warehouse[] = [];
  selectedWarehouseId: number | string | null = '';
  selectedType: string | null = '';
  selectedStatus: string | null = '';
  searchTerm = '';
  typeOptions: string[] = [
    'Peça de Moto',
    'Acessório de Segurança',
    'Ferramenta',
    'Produto de Limpeza',
    'Equipamento',
    'Suprimento',
    'Documento',
    'Outros'
  ];
  statusOptions: Status[] = [];
  selectedStock: Stock | null = null;
  showWarehouseModal = false;
  loading = false;
  error: string | null = null;

  // Paginação
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  constructor(
    private router: Router,
    private stockService: StockService,
    private warehousesService: WarehousesService,
    private statusService: StatusService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    if (!this.permissionService.hasAnchor(ModuleGroup.Estoque)) {
      this.router.navigate(['/dashboard']);
      return;
    }

    this.loadStocks();
    this.loadWarehouses();
    this.loadStatuses();
  }

  loadStatuses(): void {
    this.statusService.getByEntity('Stock').subscribe({
      next: (statuses) => {
        this.statusOptions = statuses;
      },
      error: (err) => {
        console.error('Erro ao carregar status', err);
        this.statusOptions = [];
      }
    });
  }

  loadStocks(): void {
    this.fetchStocks(false);
  }

  private fetchStocks(resetPage: boolean): void {
    this.loading = true;
    this.error = null;
    if (resetPage) this.currentPage = 1;

    const hasWarehouse = this.selectedWarehouseId !== null && this.selectedWarehouseId !== '' && Number(this.selectedWarehouseId) > 0;
    const hasType      = !!this.selectedType;
    const hasName      = !!this.searchTerm;
    const hasStatus    = !!this.selectedStatus;
    const activeCount  = [hasWarehouse, hasType, hasName, hasStatus].filter(Boolean).length;

    // Status has no API endpoint; 2+ active filters cause pagination mismatch — load all and paginate client-side
    const needsClientSide = hasStatus || activeCount > 1;
    const fetchOptions: PaginationOptions = needsClientSide
      ? { page: 1, pageSize: 1000 }
      : { page: this.currentPage, pageSize: this.pageSize };

    this.resolveListRequest(fetchOptions).subscribe({
      next: (result: StockPagedResult) => {
        const filtered = this.applyClientSideFilters(result.items || []);
        this.stocks = filtered;
        if (needsClientSide) {
          this.totalItems    = 0; // app-table uses internal pagination over full array
          this.hasNextPage   = false;
          this.hasPreviousPage = false;
        } else {
          this.totalItems    = result.totalItems;
          this.totalPages    = result.totalPages;
          this.currentPage   = result.page;
          this.hasNextPage   = this.currentPage < this.totalPages;
          this.hasPreviousPage = this.currentPage > 1;
        }
        this.loading = false;
      },
      error: (error) => {
        this.error = `Erro ao carregar estoques: ${error.message || 'Erro desconhecido'}`;
        this.loading = false;
        this.stocks = [];
      }
    });
  }

  loadWarehouses(): void {
    this.warehousesService.getAll({ page: 1, pageSize: 1000 }).subscribe({
      next: (result) => { this.warehouses = result.items || []; },
      error: _ => { this.warehouses = []; }
    });
  }

  onWarehouseFilterChange(warehouseId: number | string | null): void {
    this.selectedWarehouseId = warehouseId;
    this.applyFilters();
  }

  onTypeFilterChange(type: string | null): void {
    this.selectedType = type;
    this.applyFilters();
  }

  onStatusFilterChange(status: string | null): void {
    this.selectedStatus = status;
    this.applyFilters();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.fetchStocks(false);
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.fetchStocks(false);
  }

  onSearchChange(term: string): void {
    const next = (term || '').trim();
    if (next === this.searchTerm) {
      return;
    }
    this.searchTerm = next;
    this.fetchStocks(true);
  }

  clearFilters(): void {
    this.selectedWarehouseId = '';
    this.selectedType = '';
    this.selectedStatus = '';
    this.searchTerm = '';
    this.fetchStocks(true);
  }

  private applyFilters(): void {
    this.fetchStocks(true);
  }

  private resolveListRequest(options: PaginationOptions): Observable<StockPagedResult> {
    const hasWarehouseFilter = this.selectedWarehouseId !== null && this.selectedWarehouseId !== '' && Number(this.selectedWarehouseId) > 0;
    const hasTypeFilter = !!this.selectedType;
    const hasNameFilter = !!this.searchTerm;

    // Busca por nome tem prioridade: usa GET /stock?name=...&page=...&pageSize=...
    // (warehouse/type ficam aplicados client-side enquanto há nome).
    if (hasNameFilter) {
      return this.stockService.getAll(options, this.searchTerm);
    }

    if (hasWarehouseFilter) {
      return this.stockService.getByWarehouse(Number(this.selectedWarehouseId), options);
    }

    if (hasTypeFilter) {
      return this.stockService.getByType(this.selectedType as string, options);
    }

    return this.stockService.getAll(options);
  }

  private applyClientSideFilters(items: Stock[]): Stock[] {
    return items.filter((stock) => {
      const matchesWarehouse = !this.selectedWarehouseId || this.selectedWarehouseId === '' ||
        Number(stock.warehouseId) === Number(this.selectedWarehouseId);

      const matchesType = !this.selectedType ||
        (stock.type || '').toLowerCase().trim() === (this.selectedType || '').toLowerCase().trim();

      const matchesStatus = this.matchesStatusFilter(stock);

      return matchesWarehouse && matchesType && matchesStatus;
    });
  }

  private matchesStatusFilter(stock: Stock): boolean {
    if (!this.selectedStatus) {
      return true;
    }

    const selectedStatus = this.selectedStatus.toLowerCase().trim();
    const stockStatus = (stock.status || '').toLowerCase().trim();

    if (stockStatus === selectedStatus || stockStatus.includes(selectedStatus)) {
      return true;
    }

    if (selectedStatus === 'ativo') {
      return stock.isActive === true;
    }

    if (selectedStatus === 'inativo') {
      return stock.isActive === false;
    }

    return false;
  }

  // Método para lidar com cliques nas ações
  onActionClick(actionData: {action: string, item: any}): void {
    const { action, item } = actionData;
    
    switch (action) {
      case 'view':
        this.viewDetails(item);
        break;
      case 'edit':
        this.edit(item);
        break;
      case 'warehouse':
        this.manageWarehouse(item);
        break;
      case 'delete':
        this.remove(item);
        break;
      default:
    }
  }

  newStock(): void { 
    this.router.navigate(['/stock/inventory/new']); 
  }

  edit(stock: Stock): void { 
    this.router.navigate(['/stock/inventory', stock.id, 'edit']); 
  }

  remove(stock: Stock): void { 
    if (confirm(`Tem certeza que deseja excluir o estoque "${stock.name}"?`)) {
      this.stockService.delete(stock.id).subscribe({
        next: _ => {
          this.loadStocks();
        },
        error: _ => {
          // Error handling is done in the service
        }
      });
    }
  }

  viewDetails(stock: Stock): void {
    this.router.navigate(['/stock/inventory', stock.id, 'view']);
  }

  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'ativo':
        return 'badge bg-success';
      case 'inativo':
        return 'badge bg-secondary';
      case 'bloqueado':
        return 'badge bg-danger';
      case 'manutenção':
        return 'badge bg-warning';
      default:
        return 'badge bg-primary';
    }
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.stocks = [...this.stocks].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  getQuantityStatusClass(stock: Stock): string {
    if (!stock.minimumQuantity) return '';
    
    if (stock.currentQuantity <= 0) {
      return 'text-danger fw-bold';
    } else if (stock.currentQuantity <= stock.minimumQuantity) {
      return 'text-warning fw-bold';
    }
    return 'text-success';
  }

  getQuantityStatusText(stock: Stock): string {
    if (!stock.minimumQuantity) return '';
    
    if (stock.currentQuantity <= 0) {
      return 'Sem estoque';
    } else if (stock.currentQuantity <= stock.minimumQuantity) {
      return 'Estoque baixo';
    }
    return 'Estoque normal';
  }

  manageWarehouse(stock: Stock): void {
    this.selectedStock = stock;
    this.showWarehouseModal = true;
  }

  closeWarehouseModal(): void {
    this.showWarehouseModal = false;
    this.selectedStock = null;
  }

  onWarehouseOperationSuccess(): void {
    this.loadStocks(); // Recarregar a lista para mostrar as mudanças
  }
}
